import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormHelperText } from "@mui/material";
import { toast } from "react-toastify";
import { SideOverlay } from "../../../components";
import { BoothTree } from "../../../components";
import { useStore } from "../../../helpers";
import {
    FTPFormButton,
    FTPInput,
    FTPSelect,
    FormLabel,
} from "../../../components-new";
import { TabButtonGroup } from "../components";
import { taskCreateSchema, TaskCreateFormValues } from "./taskCreateSchema";

interface TaskCreateProps {
    isOpen: boolean;
    onClose: () => void;
    projectid: string | undefined;
    onRefresh?: () => void;
}

export const TaskCreate: React.FC<TaskCreateProps> = observer(
    ({ isOpen, onClose, projectid, onRefresh }) => {
        const { taskManagerStore } = useStore();

        const surveyOptions = useMemo(
            () =>
                (taskManagerStore.surveys || []).map((s: any) => ({
                    label: s.name,
                    value: String(s.id),
                })),
            [taskManagerStore.surveys]
        );

        const methods = useForm<TaskCreateFormValues>({
            defaultValues: {
                type: "individual",
                bulkScope: "all",
                surveyid: "",
                count: "",
                booth: {},
                booths: [],
            },
            resolver: yupResolver(taskCreateSchema) as any,
            mode: "onBlur",
        });

        const {
            handleSubmit,
            watch,
            reset,
            clearErrors,
            formState: { errors, isSubmitting },
        } = methods;

        const type = watch("type");
        const bulkScope = watch("bulkScope");

        // Reset form when the drawer opens/closes
        useEffect(() => {
            if (isOpen) {
                reset({
                    type: "individual",
                    bulkScope: "all",
                    surveyid: "",
                    count: "",
                    booth: {},
                    booths: [],
                });
            }
        }, [isOpen, reset]);

        const onSubmit = async (values: TaskCreateFormValues) => {
            if (!projectid) return;

            const payload = {
                projectid: parseInt(projectid, 10),
                surveyid: parseInt(values.surveyid, 10),
                count: parseInt(values.count, 10),
            };

            try {
                if (values.type === "individual") {
                    const res = await taskManagerStore.create_single_task({
                        ...payload,
                        node: values.booth,
                    });
                    if (res?.ok) toast.success("Task created successfully");
                } else if (
                    values.type === "bulk" &&
                    values.bulkScope === "all"
                ) {
                    const res =
                        await taskManagerStore.create_bulk_tasks(payload);
                    if (res?.data) toast.success("Tasks created successfully");
                } else {
                    // Placeholder API: bulk + selected booths (backend not ready yet)
                    const boothNumbers = (values.booths || [])
                        .map((b: any) => b?.value)
                        .filter(Boolean);

                    await taskManagerStore.create_bulk_tasks_selected({
                        ...payload,
                        boothNumbers,
                    });
                    toast.info(
                        "Bulk (selected booths) request sent via placeholder API"
                    );
                }

                if (onRefresh) onRefresh();
                onClose();
            } catch (e) {
                toast.error("Failed to create task(s)");
            }
        };

        const onInvalid = (invalid: any) => {
            const first = Object.values(invalid || {})[0] as any;
            const msg = first?.message;
            if (msg) toast.error(msg);
        };

        return (
            <SideOverlay
                show={isOpen}
                onClose={onClose}
                title="Create Task"
                size="medium"
            >
                <FormProvider {...methods}>
                    <form
                        onSubmit={handleSubmit(onSubmit, onInvalid)}
                        className="flex flex-col gap-6 bg-slate-50 p-4 rounded-lg h-full"
                    >
                        {/* Task Type Selection */}
                        <TabButtonGroup
                            name="type"
                            label="Task Type"
                            control={methods.control}
                            options={[
                                {
                                    label: "Individual",
                                    value: "individual",
                                    icon: "fa-user",
                                },
                                {
                                    label: "Bulk",
                                    value: "bulk",
                                    icon: "fa-layer-group",
                                },
                            ]}
                        />

                        {/* Bulk Scope Selection */}
                        {type === "bulk" && (
                            <TabButtonGroup
                                name="bulkScope"
                                label="Bulk Scope"
                                control={methods.control}
                                options={[
                                    {
                                        label: "All booths",
                                        value: "all",
                                        icon: "fa-globe",
                                    },
                                    {
                                        label: "Selected booths",
                                        value: "selected",
                                        icon: "fa-check-square",
                                    },
                                ]}
                            />
                        )}

                        <FTPSelect
                            name="surveyid"
                            label="Survey"
                            options={surveyOptions}
                        />
                        <FTPInput
                            name="count"
                            label="Interviews per Booth"
                            numericOnly
                        />

                        {/* Individual booth picker */}
                        {type === "individual" && (
                            <div>
                                <FormLabel>
                                    Select Booth{" "}
                                    <span className="text-red-500">*</span>
                                </FormLabel>
                                <Controller
                                    name="booth"
                                    render={({ field }) => (
                                        <>
                                            <BoothTree
                                                onBoothSelection={(
                                                    node: any
                                                ) => {
                                                    field.onChange(node);
                                                    clearErrors("booth");
                                                }}
                                                selected_booth={
                                                    field.value?.value || ""
                                                }
                                            />
                                            {errors.booth?.message && (
                                                <FormHelperText error>
                                                    {String(
                                                        errors.booth.message
                                                    )}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        )}

                        {/* Bulk selected booths picker */}
                        {type === "bulk" && bulkScope === "selected" && (
                            <div>
                                <FormLabel>
                                    Select Booths{" "}
                                    <span className="text-red-500">*</span>
                                </FormLabel>
                                <Controller
                                    name="booths"
                                    render={({ field }) => (
                                        <>
                                            <BoothTree
                                                isMulti
                                                onBoothSelection={(
                                                    selected: any[]
                                                ) => {
                                                    field.onChange(
                                                        selected || []
                                                    );
                                                    clearErrors("booths");
                                                }}
                                                selected_booth={
                                                    field.value || []
                                                }
                                            />
                                            {errors.booths?.message && (
                                                <FormHelperText error>
                                                    {String(
                                                        errors.booths.message
                                                    )}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        )}
                        <div className="mt-auto px-6 pb-6">
                            <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                                <FTPFormButton
                                    actionType="Cancel"
                                    handleButtonClick={onClose}
                                    type="button"
                                />
                                <FTPFormButton
                                    actionType="Save"
                                    handleButtonClick={() => {}}
                                    type="submit"
                                />
                                {isSubmitting && (
                                    <span className="ml-2 text-sm text-slate-500 flex items-center gap-2">
                                        <i className="fas fa-spinner fa-spin" />
                                        Creating...
                                    </span>
                                )}
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </SideOverlay>
        );
    }
);
